/**
 * Hjälpfunktioner för att korrekt avgöra om en kund-kommentar är publicerad
 * eller fortfarande är ett opublicerat utkast (draft).
 *
 * Bakgrund: backend sätter `parent.text` på kommentar-objekt även för utkast,
 * men markerar utkastet via `thread.entries[].draft = true`. Frontend-kod som
 * bara kollar `parent.draft` missar detta och tror att utkastet är publicerat
 * → feedback-formet döljs så fort kund autosparar en draft.
 *
 * Per-rad draft-flagga är källan till sanning. Parent-draft är legacy.
 */

/**
 * Returnerar true om kommentaren har MINST EN publicerad kund-rad i tråden
 * (eller legacy-data utan thread där parent.text är satt och parent.draft saknas).
 *
 * @param {*} val — kommentar-objekt eller string
 * @returns {boolean}
 */
export const hasPublishedCustomerComment = (val) => {
  if (val == null) return false;
  if (typeof val !== 'object') return String(val).trim().length > 0;

  // Legacy: om parent.draft är satt är HELA kommentaren ett utkast
  if (val.draft === true) return false;

  // Modern data: thread-entries har per-rad draft-flagga (källa till sanning)
  if (Array.isArray(val.thread) && val.thread.length > 0) {
    return val.thread.some(entry =>
      entry?.by === 'customer' &&
      entry?.draft !== true &&
      entry?.text &&
      String(entry.text).trim().length > 0
    );
  }

  // Ingen thread → fall tillbaka på parent.text
  return Boolean(val.text && String(val.text).trim().length > 0);
};

/**
 * Returnerar true om någon kommentar i comments-objektet är publicerad.
 *
 * @param {Object} comments — customerResponse.comments
 * @returns {boolean}
 */
export const hasAnyPublishedCustomerComment = (comments) => {
  if (!comments || typeof comments !== 'object') return false;
  return Object.values(comments).some(hasPublishedCustomerComment);
};

/**
 * Finns publicerad kundfeedback som gäller DEN HÄR versionen?
 *
 * Kommentarer ärvs in i varje ny estimatversion så att byggkonsulten ser
 * historiken. Men de hör till den version de skrevs på — utan tidskontrollen
 * tändes "Feedback skickad" direkt när en ny version skickades, på ett estimat
 * kunden aldrig svarat på.
 *
 * @param {Object} comments — customerResponse.comments
 * @param {string|Date} sentAt — när den aktuella versionen skickades
 * @returns {boolean}
 */
export const hasPublishedCustomerCommentAfter = (comments, sentAt) => {
  if (!comments || typeof comments !== 'object') return false;
  const gransvarde = sentAt ? new Date(sentAt).getTime() : 0;
  if (!Number.isFinite(gransvarde)) return false;

  return Object.values(comments).some(val => {
    if (!val || typeof val !== 'object') return false;
    if (!Array.isArray(val.thread)) return false;
    return val.thread.some(rad =>
      rad?.by === 'customer' &&
      rad?.draft !== true &&
      rad?.text &&
      rad?.at &&
      new Date(rad.at).getTime() > gransvarde
    );
  });
};

/**
 * Returnerar true om kommentaren har ett opublicerat utkast från kunden.
 * Används för "ny feedback klar att skickas"-bannern.
 *
 * @param {*} val — kommentar-objekt
 * @returns {boolean}
 */
export const hasUnpublishedCustomerDraft = (val) => {
  if (val == null || typeof val !== 'object') return false;

  // Legacy parent-draft-flagga
  if (val.draft === true && val.text && String(val.text).trim().length > 0) {
    return true;
  }

  // Modern: senaste customer-thread-entry är draft med text
  if (Array.isArray(val.thread) && val.thread.length > 0) {
    return val.thread.some(entry =>
      entry?.by === 'customer' &&
      entry?.draft === true &&
      entry?.text &&
      String(entry.text).trim().length > 0
    );
  }

  return false;
};
